// admin-client/src/components/ImportButton.jsx
// =============================================================================
// Reusable "Import CSV" button paired with a hidden file input. Pops up a
// result toast/modal showing inserted / updated / errors counts. Use on any
// admin page that has a matching POST /api/imports/* endpoint.
//
//   <ImportButton onImport={(file) => api.imports.devices(file)}
//                 onAfter={refresh}>
//     Import CSV
//   </ImportButton>
// =============================================================================

import { useRef, useState } from 'react';

export default function ImportButton({ onImport, onAfter, children, className = '' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await onImport(file);
      setResult(r);
      onAfter?.();
    } catch (e) {
      setErr(e.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onPick}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={
          'bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 px-3 py-2 rounded text-sm ' +
          className
        }
      >
        {busy ? 'Importing…' : children || 'Import CSV'}
      </button>

      {(result || err) && (
        <div className="fixed inset-0 z-40 bg-black/30 flex items-center justify-center p-4" onClick={() => { setResult(null); setErr(null); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-3">
            <header className="flex items-center justify-between">
              <h3 className="font-semibold text-cathay-700">Import result</h3>
              <button onClick={() => { setResult(null); setErr(null); }} className="text-slate-500 hover:text-slate-800 text-xl">×</button>
            </header>
            {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{err}</div>}
            {result && (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Rows" value={result.total} />
                  <Stat label="Inserted" value={result.inserted} tone="green" />
                  <Stat label="Updated" value={result.updated} tone="cathay" />
                </div>
                {Array.isArray(result.errors) && result.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 text-xs">
                    <div className="font-medium text-red-800">{result.errors.length} row(s) failed:</div>
                    <ul className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                      {result.errors.slice(0, 20).map((e, i) => (
                        <li key={i} className="font-mono text-red-700">{e.error}</li>
                      ))}
                      {result.errors.length > 20 && (
                        <li className="text-red-700 italic">… and {result.errors.length - 20} more</li>
                      )}
                    </ul>
                  </div>
                )}
                {Array.isArray(result.new_user_setup_links) && result.new_user_setup_links.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs space-y-1">
                    <div className="font-medium text-amber-900">
                      {result.new_user_setup_links.length} new user(s) — setup links shown once:
                    </div>
                    <ul className="max-h-40 overflow-y-auto space-y-1">
                      {result.new_user_setup_links.map((l) => (
                        <li key={l.user_id} className="font-mono">
                          {l.email}: <span className="text-amber-700">
                            {window.location.origin}/setup-account?token={l.setup_token}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <button onClick={() => { setResult(null); setErr(null); }} className="bg-cathay-500 hover:bg-cathay-600 text-white text-sm px-3 py-1.5 rounded w-full">
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    green: 'bg-green-50 text-green-800',
    cathay: 'bg-cathay-50 text-cathay-800',
  };
  return (
    <div className={'rounded p-2 ' + (tone ? tones[tone] : 'bg-slate-100 text-slate-800')}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-medium">{value}</div>
    </div>
  );
}
